/**
 * Default layout for the Closers page.
 *
 * Replicates the hardcoded layout:
 * - 4 MetricCards (Cash Collected, Revenue Generated, MRR, New Cash excl MRR)
 * - 1 Commission card (10%, indigo theme)
 * - 5 StatCards (Show Rate, Close Rate, Calls Due, Calls Taken, Calls Closed)
 * - 4 StatCards (Avg Cash/Call, Cash/Close, Cash/Application, Cash/Opt-in)
 * - 1 areaChart (Close Rate Trend) + 1 pieChart (Deal Status) side-by-side
 * - 1 table (Closer Performance)
 */

import type { DashboardLayout } from "../types";

export const CLOSERS_DEFAULT: DashboardLayout = {
  widgets: [
    // --- Row 1: Metric Cards (y:0) ---
    {
      id: "closers-metric-cash",
      type: "metric",
      title: "Cash Collected",
      config: {
        metricKey: "totalCashCollected",
        format: "currency",
        icon: "dollar",
        color: "brand",
      },
    },
    {
      id: "closers-metric-revenue",
      type: "metric",
      title: "Revenue Generated",
      config: {
        metricKey: "totalRevenue",
        format: "currency",
        icon: "trending",
        color: "green",
      },
    },
    {
      id: "closers-metric-mrr",
      type: "metric",
      title: "MRR",
      config: {
        metricKey: "mrrCash",
        format: "currency",
        icon: "chart",
        color: "blue",
      },
    },
    {
      id: "closers-metric-newcash",
      type: "metric",
      title: "New Cash (excl. MRR)",
      config: {
        metricKey: "newCashCollected",
        format: "currency",
        icon: "dollar",
        color: "brand",
      },
    },

    // --- Row 2: Commission Card (y:4) ---
    {
      id: "closers-commission",
      type: "commission",
      title: "Closer Commission (10%)",
      config: {
        cashKey: "totalCashCollected",
        rate: 0.1,
        colorTheme: "indigo",
        description: "10% of total cash collected",
      },
    },

    // --- Row 3: Performance Stats (y:8) ---
    {
      id: "closers-stat-showrate",
      type: "stat",
      title: "Show Rate",
      config: { metricKey: "showRate", format: "percent" },
    },
    {
      id: "closers-stat-closerate",
      type: "stat",
      title: "Close Rate",
      config: { metricKey: "closeRate", format: "percent" },
    },
    {
      id: "closers-stat-callsdue",
      type: "stat",
      title: "Calls Due",
      config: { metricKey: "callsDue", format: "number" },
    },
    {
      id: "closers-stat-callstaken",
      type: "stat",
      title: "Calls Taken",
      config: { metricKey: "callsTaken", format: "number" },
    },
    {
      id: "closers-stat-callsclosed",
      type: "stat",
      title: "Calls Closed",
      config: { metricKey: "callsClosed", format: "number" },
    },

    // --- Row 4: Average Stats (y:10) ---
    {
      id: "closers-stat-avgcashcall",
      type: "stat",
      title: "Avg. Cash / Call",
      config: { metricKey: "avgCashPerCall", format: "currency" },
    },
    {
      id: "closers-stat-avgcashclose",
      type: "stat",
      title: "Avg. Cash / Close",
      config: { metricKey: "avgCashPerClose", format: "currency" },
    },
    {
      id: "closers-stat-avgcashapp",
      type: "stat",
      title: "Avg. Cash / Application",
      config: { metricKey: "avgCashPerApplication", format: "currency" },
    },
    {
      id: "closers-stat-avgcashoptin",
      type: "stat",
      title: "Avg. Cash / Opt-in",
      config: { metricKey: "avgCashPerOptin", format: "currency" },
    },

    // --- Row 5: Charts (y:12) ---
    {
      id: "closers-area-closerate",
      type: "areaChart",
      title: "Close Rate Trend",
      config: {
        dataKey: "closeRateTrend",
        title: "Close Rate Trend",
        yFormat: "percent",
        tooltipFormat: "percent",
      },
    },
    {
      id: "closers-pie-dealstatus",
      type: "pieChart",
      title: "Deal Status Breakdown",
      config: { dataKey: "outcomeBreakdown", title: "Deal Status Breakdown" },
    },

    // --- Row 6: Table (y:24) ---
    {
      id: "closers-table-performance",
      type: "table",
      title: "Closer Performance",
      config: { dataKey: "closerPerformance", title: "Closer Performance" },
    },
  ],

  gridLayout: [
    // Row 1: 4 metric cards
    { i: "closers-metric-cash", x: 0, y: 0, w: 3, h: 3 },
    { i: "closers-metric-revenue", x: 3, y: 0, w: 3, h: 3 },
    { i: "closers-metric-mrr", x: 6, y: 0, w: 3, h: 3 },
    { i: "closers-metric-newcash", x: 9, y: 0, w: 3, h: 3 },

    // Row 2: commission card (full-width)
    { i: "closers-commission", x: 0, y: 3, w: 12, h: 3 },

    // Row 3: 5 stat cards (2+2+3+3+2 = 12)
    { i: "closers-stat-showrate", x: 0, y: 6, w: 2, h: 2 },
    { i: "closers-stat-closerate", x: 2, y: 6, w: 2, h: 2 },
    { i: "closers-stat-callsdue", x: 4, y: 6, w: 3, h: 2 },
    { i: "closers-stat-callstaken", x: 7, y: 6, w: 3, h: 2 },
    { i: "closers-stat-callsclosed", x: 10, y: 6, w: 2, h: 2 },

    // Row 4: 4 average stat cards
    { i: "closers-stat-avgcashcall", x: 0, y: 8, w: 3, h: 2 },
    { i: "closers-stat-avgcashclose", x: 3, y: 8, w: 3, h: 2 },
    { i: "closers-stat-avgcashapp", x: 6, y: 8, w: 3, h: 2 },
    { i: "closers-stat-avgcashoptin", x: 9, y: 8, w: 3, h: 2 },

    // Row 5: area chart + pie chart side-by-side
    { i: "closers-area-closerate", x: 0, y: 10, w: 6, h: 12 },
    { i: "closers-pie-dealstatus", x: 6, y: 10, w: 6, h: 12 },

    // Row 6: table
    { i: "closers-table-performance", x: 0, y: 22, w: 12, h: 10 },
  ],
};
